"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import ArrowIcon from "@/components/icons/arrow";
import { cn } from "@/lib/utils";
import type { Consulting } from "@/types/dashboard";

const CARD_WIDTH = 288;
const GAP = 16;

const ConsultingCard = ({
  consulting,
  isActive,
}: {
  consulting: Consulting;
  isActive: boolean;
}) => (
  <div
    className={cn(
      "bg-white rounded-2xl p-6 flex flex-col gap-4 shrink-0 transition-transform duration-300 ease-out",
      isActive ? "scale-105" : "scale-95 opacity-60",
    )}
    style={{ width: CARD_WIDTH }}
  >
    <div className="flex items-center justify-center">
      {consulting.image && (
        <Image
          alt={consulting.title}
          className="object-cover rounded-full w-40 h-40"
          height={160}
          src={consulting.image}
          width={160}
        />
      )}
    </div>
    <div className="flex flex-col gap-1">
      <h3 className="font-bold text-base leading-tight">{consulting.title}</h3>
      <p className="text-sm text-neutral-500 line-clamp-2">
        {consulting.summary}
      </p>
    </div>
    <Link
      className="bg-brand-blue-600 text-white text-sm font-medium rounded-full py-3 px-6 flex items-center justify-center gap-2 w-full cursor-pointer"
      href={`/${consulting.slug}`}
    >
      Detaylı Bilgi <ArrowIcon className="stroke-white w-4 h-4" />
    </Link>
  </div>
);

export const ConsultingCarousel = ({
  consultings,
}: {
  consultings: Consulting[];
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(() => setContainerWidth(el.offsetWidth));
    ro.observe(el);
    setContainerWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  const translateX = containerWidth
    ? (containerWidth - CARD_WIDTH) / 2 - currentIndex * (CARD_WIDTH + GAP)
    : 0;

  return (
    <div
      className="bg-neutral-100 h-full rounded-2xl flex flex-col justify-between py-8 gap-6 overflow-hidden"
      ref={containerRef}
    >
      <div
        className="flex transition-transform duration-300 ease-out"
        style={{ gap: GAP, transform: `translateX(${translateX}px)` }}
      >
        {consultings.map((c, i) => (
          <ConsultingCard
            consulting={c}
            isActive={i === currentIndex}
            key={c.id}
          />
        ))}
      </div>
      <div className="flex justify-end gap-2 px-6">
        <button
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center disabled:opacity-30 cursor-pointer disabled:cursor-default transition-opacity"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
          type="button"
        >
          <ArrowIcon className="stroke-brand-blue-600 rotate-180" />
        </button>
        <button
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center disabled:opacity-30 cursor-pointer disabled:cursor-default transition-opacity"
          disabled={currentIndex === consultings.length - 1}
          onClick={() =>
            setCurrentIndex((i) => Math.min(consultings.length - 1, i + 1))
          }
          type="button"
        >
          <ArrowIcon className="stroke-brand-blue-600" />
        </button>
      </div>
    </div>
  );
};
